// Recovery for failed JavaScript/CSS chunk loads.
//
// A browser that loaded the page from an earlier deploy may request a chunk that
// is slow to arrive (network timeout) or no longer on disk (a clean rebuild ran).
// Either way the page is recoverable: a full reload pulls the current build's HTML,
// which references chunks that are actually being served. We reload at most once per
// short window so a genuinely broken build can't trap the user in a reload loop.

const RELOAD_GUARD_KEY = 'penzu:chunkReloadAt';
const RELOAD_GUARD_WINDOW_MS = 10_000;

export function isChunkLoadError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const name = 'name' in error ? String((error as { name?: unknown }).name) : '';
  const message =
    'message' in error ? String((error as { message?: unknown }).message) : '';
  return (
    name === 'ChunkLoadError' ||
    /Loading (?:CSS )?chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

// Reloads the page when `error` is a chunk-load failure. Returns true if a reload was
// triggered, so a caller that reports manually (global-error.tsx) can skip its report and
// error UI. (The Bugsnag React boundary reports in componentDidCatch before its fallback
// mounts, so that path still records one report — recovery there only reloads.)
// Returns false for non-chunk errors, or if we already reloaded for a chunk error very
// recently — in that case the new build is also failing and the caller should show its
// error UI / report the error instead of looping.
export function recoverFromChunkLoadError(error: unknown): boolean {
  if (typeof window === 'undefined' || !isChunkLoadError(error)) return false;

  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_GUARD_KEY) ?? '0');
    if (Date.now() - last < RELOAD_GUARD_WINDOW_MS) return false;
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  } catch {
    // sessionStorage can be unavailable (private mode / quota). Proceed with a single
    // reload; without persistence we can't loop-guard, but a recurring chunk error that
    // also defeats storage is unlikely.
  }

  window.location.reload();
  return true;
}
